import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { productKeys } from './useProducts';
import type { BulkUpdateInput, BulkResult, ImportResult } from '../types';

// Bulk update products
export function useBulkUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkUpdateInput) =>
      api.post<BulkResult>('/products/bulk/update', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
}

// Bulk delete products
export function useBulkDelete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productIds: string[]) =>
      api.post<BulkResult>('/products/bulk/delete', { productIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
}

// Bulk move products to another store
export function useBulkMove() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productIds, targetStoreId }: { productIds: string[]; targetStoreId: string }) =>
      api.post<BulkResult>('/products/bulk/move', { productIds, targetStoreId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
}

// Bulk duplicate products
export function useBulkDuplicate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productIds: string[]) =>
      api.post<BulkResult>('/products/bulk/duplicate', { productIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
}

// Import products from CSV
export function useImportProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ csvData, storeId, categoryId, updateExisting }: {
      csvData: string;
      storeId?: string;
      categoryId?: string;
      updateExisting?: boolean;
    }) =>
      api.post<ImportResult>('/products/import', { csvData, storeId, categoryId, updateExisting }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
}

// Export products to CSV
export function useExportProducts() {
  return useMutation({
    mutationFn: async ({ storeId, categoryId, status, format = 'csv' }: {
      storeId?: string;
      categoryId?: string;
      status?: 'active' | 'inactive' | 'all';
      format?: 'csv' | 'json';
    }) => {
      const queryParams = new URLSearchParams();
      if (storeId) queryParams.set('storeId', storeId);
      if (categoryId) queryParams.set('categoryId', categoryId);
      if (status) queryParams.set('status', status);
      queryParams.set('format', format);

      const response = await api.get<string | { products: any[] }>(
        `/products/export?${queryParams.toString()}`
      );

      return response;
    },
  });
}

// Download import template
export function useDownloadTemplate() {
  return useMutation({
    mutationFn: async () => {
      const response = await api.get<string>('/products/import/template');
      return response;
    },
  });
}
